import PropTypes from "prop-types";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
// @mui
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
// locales
import { useTranslation } from "react-i18next";
// components
import Iconify from "src/components/iconify";
//
import {
  DEFAULT_LOCALE,
  DEFAULT_OPERATION,
  DEFAULT_PAGE_SIZE,
  ROW_CONTROL_SELECTOR,
  ROW_HEIGHT,
  STATE_SAVING_DELAY,
  TREE_DRAG_THRESHOLD,
  TREE_TOUCH_DRAG_DELAY,
} from "./constants";
import { parseGridChildren } from "./config";
import { GridRoot } from "./styles";
import {
  buildViewColumns,
  columnsMap,
  computeLayout,
  parseWidth,
  uniqueColumns,
} from "./core/layout";
import { createEmitter } from "./core/emitter";
import { readStorage, writeStorage } from "./core/storage";
import {
  applySavedState,
  createInitialState,
  gridStateReducer,
  initialColumnState,
  toSavedState,
} from "./core/state";
import {
  buildDataSourceFilter,
  cloneFilter,
  getColumnSelector,
  getFilterRowCondition,
  getHeaderFilterCondition,
  setFilterRowCondition,
  setHeaderFilterCondition,
} from "./core/filter";
import { calculateCellValue, getFieldValue, getValueText, isDateType } from "./core/format";
import { treeKey } from "./core/tree";
import { useTreeData } from "./core/tree-data";
import { resolveSource } from "./store/data-source";
import { queryLocal } from "./store/local-query";
import { TreeDataRow } from "./components/tree-rows";
import HeaderRow from "./components/header-row";
import FilterRow from "./components/filter-row";
import GridToolbar from "./components/grid-toolbar";
import ColumnMenu from "./components/column-menu";
import ColumnChooser from "./components/column-chooser";
import HeaderFilterPopover from "./components/header-filter";
import FilterPanel from "./components/filter-panel";
import FilterBuilderDialog from "./components/filter-builder";
import Pager from "./components/pager";

const DEFAULT_TOOLBAR_ITEMS = [{ name: "columnChooserButton", location: "after" }];

const NO_DRAG_SELECTOR = `${ROW_CONTROL_SELECTOR}, .cg-tree-toggle`;

function createDragGhost(rowElement, root) {
  const source = rowElement.querySelector(".cg-tree-value") || rowElement.cells[0];
  const rootStyle = window.getComputedStyle(root);
  const ghost = document.createElement("div");

  Object.assign(ghost.style, {
    position: "fixed",
    left: "0px",
    top: "0px",
    zIndex: "2000",
    display: "flex",
    alignItems: "center",
    maxWidth: "360px",
    padding: "4px 12px",
    overflow: "hidden",
    whiteSpace: "nowrap",
    pointerEvents: "none",
    borderRadius: "8px",
    opacity: "0.95",
    font: rootStyle.font,
    color: rootStyle.color,
    backgroundColor: rootStyle.getPropertyValue("--cg-paper") || rootStyle.backgroundColor,
    boxShadow: "0 8px 16px rgba(0, 0, 0, 0.24)",
  });

  if (source) ghost.appendChild(source.cloneNode(true));

  document.body.appendChild(ghost);

  return ghost;
}

const TreeList = forwardRef(function TreeList(props, ref) {
  const {
    children,
    dataSource,
    keyExpr,
    parentIdExpr = "parentId",
    rootValue = null,
    height,
    width,
    id,
    className,
    style,
    showBorders = false,
    showColumnLines = false,
    showRowLines = true,
    rowAlternationEnabled = false,
    hoverStateEnabled = true,
    allowColumnResizing = false,
    allowColumnReordering = false,
    noDataText,
    filterValue: filterValueProp,
    defaultFilterValue,
    toolbarItems,
    locale: localeProp,
    filterMode = "withAncestors",
    autoExpandAll = false,
    expandNodesOnFiltering = true,
    defaultExpandedRowKeys,
    defaultSelectedRowKeys,
    onRowClick,
    onRowPrepared,
  } = props;

  const { t, i18n } = useTranslation();
  const locale = useMemo(
    () => ({ ...DEFAULT_LOCALE, blankText: t("gridBlank"), ...localeProp }),
    [localeProp, t]
  );

  const parsed = useMemo(() => parseGridChildren(children), [children]);
  const columns = useMemo(() => uniqueColumns(parsed.columns), [parsed.columns]);
  const childOptions = parsed.options;

  const paging = childOptions.paging || {};
  const pagerOptions = childOptions.pager || {};
  const scrollingMode = childOptions.scrolling?.mode;
  const paged = paging.enabled !== false && scrollingMode !== "virtual";
  const initialPageSize = paging.pageSize ?? paging.defaultPageSize ?? DEFAULT_PAGE_SIZE;
  const allowedPageSizesKey = JSON.stringify(pagerOptions.allowedPageSizes ?? null);
  const allowedPageSizes = useMemo(() => {
    const configured = JSON.parse(allowedPageSizesKey);

    return Array.isArray(configured)
      ? configured
      : [Math.max(Math.round(initialPageSize / 2), 1), initialPageSize, initialPageSize * 2];
  }, [allowedPageSizesKey, initialPageSize]);
  const filterRowVisible = !!childOptions.filterRow?.visible;
  const headerFilterVisible = !!childOptions.headerFilter?.visible;
  const columnChooserEnabled = !!childOptions.columnChooser?.enabled;
  const filterPanelVisible = !!childOptions.filterPanel?.visible;
  const columnFixingEnabled = !!childOptions.columnFixing?.enabled;
  const sortingMode = childOptions.sorting?.mode ?? "single";
  const selectionOptions = childOptions.selection || {};
  const selectionMode = selectionOptions.mode || "none";
  const rowDragging = childOptions.rowDragging || {};
  const rowDraggingEnabled = !!rowDragging.allowReordering;
  const stateStoring = childOptions.stateStoring || {};
  const storageKey =
    stateStoring.enabled && stateStoring.storageKey ? stateStoring.storageKey : null;
  const loadPanelEnabled = childOptions.loadPanel?.enabled !== false;

  const savedRef = useRef(undefined);

  if (savedRef.current === undefined)
    savedRef.current = storageKey ? readStorage(storageKey) : null;

  const [state, dispatch] = useReducer(gridStateReducer, null, () => {
    const initial = createInitialState({
      columns,
      filterValue: filterValueProp,
      defaultFilterValue,
      pageSize: initialPageSize,
    });

    return savedRef.current
      ? applySavedState(initial, savedRef.current, columns, allowedPageSizes)
      : initial;
  });

  const [refreshToken, setRefreshToken] = useState(0);
  const [renderVersion, setRenderVersion] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [natural, setNatural] = useState({ key: null, widths: {} });
  const [selectedKeys, setSelectedKeys] = useState(() =>
    (savedRef.current?.selectedRowKeys?.length
      ? savedRef.current.selectedRowKeys
      : defaultSelectedRowKeys || []
    ).slice()
  );
  const [menuTarget, setMenuTarget] = useState(null);
  const [headerFilterTarget, setHeaderFilterTarget] = useState(null);
  const [chooserAnchor, setChooserAnchor] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [dragState, setDragState] = useState(null);

  const propsRef = useRef(props);
  const stateRef = useRef(state);
  const columnsRef = useRef(columns);
  const handlersRef = useRef({ onRowPrepared });
  const selectedKeysRef = useRef(selectedKeys);
  const selectionModeRef = useRef(selectionMode);
  const rowDraggingRef = useRef(rowDragging);
  const localeRef = useRef(locale);
  const tRef = useRef(t);
  const rootRef = useRef(null);
  const scrollRef = useRef(null);
  const tableRef = useRef(null);
  const bodyRef = useRef(null);
  const emitterRef = useRef(null);
  const instanceRef = useRef(null);
  const draggedColumnRef = useRef(null);
  const selectedOperationsRef = useRef(new Map());
  const rowVersionsRef = useRef(new Map());
  const refreshWaitersRef = useRef([]);
  const lastClickedIndexRef = useRef(null);
  const previousColumnPropsRef = useRef(null);
  const storageKeyRef = useRef(storageKey);
  const allowedPageSizesRef = useRef(allowedPageSizes);
  const suppressClickRef = useRef(false);

  if (!emitterRef.current) emitterRef.current = createEmitter();
  if (!instanceRef.current) instanceRef.current = {};

  propsRef.current = props;
  stateRef.current = state;
  columnsRef.current = columns;
  handlersRef.current = { onRowPrepared };
  selectionModeRef.current = selectionMode;
  rowDraggingRef.current = rowDragging;
  localeRef.current = locale;
  tRef.current = t;
  storageKeyRef.current = storageKey;
  allowedPageSizesRef.current = allowedPageSizes;

  const getColumnState = useCallback(
    column => stateRef.current.columns[column.id] ?? initialColumnState(column, column.index),
    []
  );

  const columnsKey = columns.map(column => column.id).join("|");

  useLayoutEffect(() => {
    dispatch({ type: "syncColumns", columns: columnsRef.current });
  }, [columnsKey]);

  useLayoutEffect(() => {
    const previous = previousColumnPropsRef.current;
    const next = new Map();

    columns.forEach(column => {
      const snapshot = {
        visible: column.visible,
        sortOrder: column.sortOrder,
        fixed: column.fixed,
        fixedPosition: column.fixedPosition,
      };
      const before = previous?.get(column.id);

      next.set(column.id, snapshot);

      if (!before) return;

      const patch = {};

      if (before.visible !== snapshot.visible) patch.visible = snapshot.visible !== false;
      if (before.sortOrder !== snapshot.sortOrder) patch.sortOrder = snapshot.sortOrder;
      if (before.fixed !== snapshot.fixed) patch.fixed = !!snapshot.fixed;
      if (before.fixedPosition !== snapshot.fixedPosition && snapshot.fixedPosition)
        patch.fixedPosition = snapshot.fixedPosition;

      if (Object.keys(patch).length) dispatch({ type: "columnOption", id: column.id, patch });
    });

    previousColumnPropsRef.current = next;
  }, [columns]);

  const source = useMemo(() => resolveSource(dataSource, keyExpr), [dataSource, keyExpr]);
  const sourceRef = useRef(source);

  sourceRef.current = source;

  const columnsBySelector = useMemo(() => columnsMap(columns), [columns]);
  const columnsBySelectorRef = useRef(columnsBySelector);

  columnsBySelectorRef.current = columnsBySelector;

  const getGetter = useCallback(selector => {
    if (typeof selector === "function") return selector;

    const column = columnsBySelectorRef.current[selector];

    if (column?.calculateCellValue) return item => calculateCellValue(column, item);

    return item => getFieldValue(item, selector);
  }, []);

  const orderedColumns = useMemo(
    () =>
      columns
        .slice()
        .sort(
          (a, b) =>
            (state.columns[a.id]?.order ?? a.index) - (state.columns[b.id]?.order ?? b.index) ||
            a.index - b.index
        ),
    [columns, state.columns]
  );

  const sort = useMemo(
    () =>
      orderedColumns
        .filter(column => state.columns[column.id]?.sortOrder && column.hasData)
        .sort((a, b) => (state.columns[a.id].sortIndex ?? 0) - (state.columns[b.id].sortIndex ?? 0))
        .map(column => ({
          selector: getColumnSelector(column),
          desc: state.columns[column.id].sortOrder === "desc",
        })),
    [orderedColumns, state.columns]
  );

  const filter = useMemo(
    () =>
      state.filterEnabled
        ? buildDataSourceFilter(state.filterValue, columnsBySelector, "filterRow")
        : null,
    [columnsBySelector, state.filterEnabled, state.filterValue]
  );

  const initialExpandedKeys = useMemo(
    () =>
      savedRef.current ? savedRef.current.expandedRowKeys || [] : defaultExpandedRowKeys || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const data = useTreeData({
    source,
    keyExpr: source.key,
    parentIdExpr,
    rootValue,
    filter,
    sort,
    filterMode,
    getGetter,
    refreshToken,
    paged,
    pageSize: state.pageSize,
    pageIndex: state.pageIndex,
    autoExpandAll,
    expandNodesOnFiltering,
    initialExpandedKeys,
  });

  const dataRef = useRef(data);

  dataRef.current = data;

  useEffect(() => {
    if (paged && state.pageIndex > data.pageCount - 1)
      dispatch({ type: "pageIndex", pageIndex: data.pageCount - 1 });
  }, [data.pageCount, paged, state.pageIndex]);

  const scrollResetKey = JSON.stringify([filter, sort, state.pageIndex, state.pageSize]);

  useLayoutEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [scrollResetKey]);

  useLayoutEffect(() => {
    const headerRow = tableRef.current?.querySelector("thead tr.cg-header-row");
    const root = rootRef.current;

    if (!headerRow || !root) return;

    const value = `${headerRow.getBoundingClientRect().height}px`;

    if (root.style.getPropertyValue("--cg-header-height") !== value)
      root.style.setProperty("--cg-header-height", value);
  });

  const buildSavedState = useCallback(() => {
    const saved = toSavedState(stateRef.current, columnsRef.current, {
      allowedPageSizes: allowedPageSizesRef.current,
      selectedRowKeys: selectedKeysRef.current,
    });

    if (!propsRef.current.autoExpandAll) saved.expandedRowKeys = dataRef.current.getExpandedKeys();

    return saved;
  }, []);

  useEffect(() => {
    if (!storageKey) return undefined;

    const timer = setTimeout(() => writeStorage(storageKey, buildSavedState()), STATE_SAVING_DELAY);

    return () => clearTimeout(timer);
  }, [buildSavedState, data.version, selectedKeys, state, storageKey]);

  const visibleDataColumns = useMemo(
    () =>
      orderedColumns.filter(column => {
        const columnState = state.columns[column.id];

        return (
          !column.isSelection && (columnState ? columnState.visible : column.visible !== false)
        );
      }),
    [orderedColumns, state.columns]
  );

  const measureKey = `${visibleDataColumns.map(column => column.id).join("|")}#${
    data.rows.length > 0 ? 1 : 0
  }`;
  const measuring = natural.key !== measureKey && containerWidth > 0;

  const layout = useMemo(
    () =>
      computeLayout({
        dataColumns: visibleDataColumns,
        commandWidth: 0,
        getState: column => state.columns[column.id] ?? initialColumnState(column, column.index),
        natural: natural.widths,
        containerWidth,
      }),
    [containerWidth, natural.widths, state.columns, visibleDataColumns]
  );

  const viewColumns = useMemo(() => {
    const list = buildViewColumns([], visibleDataColumns, state.columns, layout);
    const treeColumn = list.find(viewColumn => viewColumn.kind === "data");

    if (treeColumn) treeColumn.treeColumn = true;

    return list;
  }, [layout, state.columns, visibleDataColumns]);

  const viewColumnsRef = useRef(viewColumns);
  const visibleColumnsRef = useRef(visibleDataColumns);
  const layoutRef = useRef(layout);

  viewColumnsRef.current = viewColumns;
  visibleColumnsRef.current = visibleDataColumns;
  layoutRef.current = layout;

  useLayoutEffect(() => {
    if (!measuring || !tableRef.current) return;

    const widths = {};

    tableRef.current.querySelectorAll("thead tr.cg-header-row > th").forEach(cell => {
      const viewColumn = viewColumns[Number(cell.dataset.colIndex)];

      if (viewColumn?.kind === "data")
        widths[viewColumn.key] = Math.ceil(cell.getBoundingClientRect().width) + 2;
    });

    setNatural({ key: measureKey, widths });
  }, [measureKey, measuring, viewColumns]);

  useLayoutEffect(() => {
    const element = scrollRef.current;

    if (!element) return undefined;

    setContainerWidth(element.clientWidth);

    if (typeof ResizeObserver === "undefined") return undefined;

    let frame = null;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setContainerWidth(element.clientWidth));
    });

    observer.observe(element);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  const selectedSet = useMemo(() => new Set(selectedKeys.map(treeKey)), [selectedKeys]);

  const getNode = useCallback(key => dataRef.current.getTree().byKey.get(treeKey(key)), []);

  const getSelectedRowsData = useCallback(
    () => selectedKeysRef.current.map(key => getNode(key)?.data).filter(item => item !== undefined),
    [getNode]
  );

  const commitSelection = useCallback(
    nextKeys => {
      const previousKeys = selectedKeysRef.current;
      const previous = new Set(previousKeys.map(treeKey));
      const next = new Set(nextKeys.map(treeKey));
      const added = nextKeys.filter(key => !previous.has(treeKey(key)));
      const removed = previousKeys.filter(key => !next.has(treeKey(key)));

      if (!added.length && !removed.length) return;

      selectedKeysRef.current = nextKeys;
      setSelectedKeys(nextKeys);

      const event = {
        component: instanceRef.current,
        selectedRowKeys: nextKeys.slice(),
        selectedRowsData: getSelectedRowsData(),
        currentSelectedRowKeys: added,
        currentDeselectedRowKeys: removed,
      };

      emitterRef.current.emit("selectionChanged", event);
      propsRef.current.onSelectionChanged?.(event);
    },
    [getSelectedRowsData]
  );

  const selectRows = useCallback(
    (keys, preserve) => {
      const list = (Array.isArray(keys) ? keys : [keys]).filter(key => key !== undefined);
      const { current } = selectedKeysRef;
      let nextKeys;

      if (selectionModeRef.current === "single")
        nextKeys = list.length ? [list[list.length - 1]] : [];
      else if (preserve) {
        const known = new Set(current.map(treeKey));

        nextKeys = [...current, ...list.filter(key => !known.has(treeKey(key)))];
      } else nextKeys = list.slice();

      commitSelection(nextKeys);

      return Promise.resolve(nextKeys);
    },
    [commitSelection]
  );

  const deselectRows = useCallback(
    keys => {
      const list = new Set((Array.isArray(keys) ? keys : [keys]).map(treeKey));
      const nextKeys = selectedKeysRef.current.filter(key => !list.has(treeKey(key)));

      commitSelection(nextKeys);

      return Promise.resolve(nextKeys);
    },
    [commitSelection]
  );

  const clearSelection = useCallback(() => commitSelection([]), [commitSelection]);

  const isRowSelectable = useCallback(row => {
    const handler = propsRef.current.onEditorPreparing;

    if (!handler) return true;

    const event = {
      component: instanceRef.current,
      parentType: "dataRow",
      command: "select",
      row: { data: row.data, key: row.key, node: row, level: row.level, rowType: "data" },
      editorOptions: { disabled: false, value: selectedKeysRef.current.includes(row.key) },
      cancel: false,
    };

    handler(event);

    return !event.cancel && !event.editorOptions.disabled;
  }, []);

  const selectableRows = useCallback(
    () => dataRef.current.getTree().nodes.filter(node => node.visible && isRowSelectable(node)),
    [isRowSelectable]
  );

  const toggleRowSelection = useCallback(
    (row, checked) => {
      if (!isRowSelectable(row)) return;

      if (checked) selectRows([row.key], selectionModeRef.current === "multiple");
      else deselectRows([row.key]);
    },
    [deselectRows, isRowSelectable, selectRows]
  );

  const repaint = useCallback(() => setRenderVersion(version => version + 1), []);

  const repaintRows = useCallback(indexes => {
    const { rows } = dataRef.current;

    (Array.isArray(indexes) ? indexes : [indexes]).forEach(index => {
      const row = rows[index];

      if (!row) return;

      const key = treeKey(row.key);

      rowVersionsRef.current.set(key, (rowVersionsRef.current.get(key) || 0) + 1);
    });

    setRenderVersion(version => version + 1);
  }, []);

  const refresh = useCallback(
    () =>
      new Promise(resolve => {
        refreshWaitersRef.current.push({ resolve, version: dataRef.current.version });
        setRefreshToken(token => token + 1);
      }),
    []
  );

  useEffect(() => {
    if (data.phase !== "idle" || !refreshWaitersRef.current.length) return;

    const ready = refreshWaitersRef.current.filter(
      waiter => waiter.version < data.version || data.error
    );

    refreshWaitersRef.current = refreshWaitersRef.current.filter(waiter => !ready.includes(waiter));
    ready.forEach(waiter => waiter.resolve());
  }, [data.error, data.phase, data.version]);

  const setFilterValue = useCallback(
    filterValue => dispatch({ type: "filterValue", filterValue: cloneFilter(filterValue ?? null) }),
    []
  );

  const clearFilter = useCallback(
    () => setFilterValue(propsRef.current.defaultFilterValue ?? null),
    [setFilterValue]
  );

  const combinedFilter = useCallback(() => {
    const { current } = stateRef;

    if (!current.filterEnabled) return null;

    return buildDataSourceFilter(current.filterValue, columnsMap(columnsRef.current), "filterRow");
  }, []);

  const getItems = useCallback(() => dataRef.current.getTree().nodes.map(node => node.data), []);

  const setColumnOption = useCallback((column, patch) => {
    dispatch({ type: "columnOption", id: column.id, patch });
  }, []);

  const findColumn = useCallback(identifier => {
    const list = columnsRef.current;

    if (typeof identifier === "number") return list[identifier];

    return list.find(
      column =>
        column.id === identifier ||
        column.name === identifier ||
        column.dataField === identifier ||
        column.caption === identifier
    );
  }, []);

  const createInitialStateNow = useCallback(
    () =>
      createInitialState({
        columns: columnsRef.current,
        filterValue: undefined,
        defaultFilterValue: propsRef.current.defaultFilterValue,
        pageSize: initialPageSize,
      }),
    [initialPageSize]
  );

  const storeFacade = useMemo(
    () => ({
      key: () => source.key,
      load: async (loadOptions = {}) => queryLocal(getItems(), loadOptions, getGetter).data,
      byKey: async key => getNode(key)?.data,
      _array: source.kind === "local" ? source.getItems() : undefined,
    }),
    [getGetter, getItems, getNode, source]
  );

  const setRowsExpanded = useCallback((key, expanded) => {
    const keys = Array.isArray(key) ? key : [key];
    const changed = dataRef.current.setExpanded(keys, expanded);

    if (changed)
      keys.forEach(item => {
        const event = { component: instanceRef.current, key: item };

        emitterRef.current.emit(expanded ? "rowExpanded" : "rowCollapsed", event);
        (expanded ? propsRef.current.onRowExpanded : propsRef.current.onRowCollapsed)?.(event);
      });

    return Promise.resolve();
  }, []);

  Object.assign(instanceRef.current, {
    refresh,
    repaint,
    repaintRows,
    getVisibleRows: () =>
      dataRef.current.rows.map((row, rowIndex) => ({
        rowType: "data",
        key: row.key,
        data: row.data,
        node: row,
        level: row.level,
        rowIndex,
        isExpanded: dataRef.current.isExpanded(row.key),
        isSelected: selectedKeysRef.current.some(key => treeKey(key) === treeKey(row.key)),
      })),
    getNodeByKey: key => getNode(key),
    getRootNode: () => dataRef.current.getTree().root,
    forEachNode: callback => dataRef.current.getTree().nodes.forEach(callback),
    getSelectedRowsData,
    getSelectedRowKeys: () => selectedKeysRef.current.slice(),
    selectRows,
    deselectRows,
    selectRowsByIndexes: indexes =>
      selectRows(
        (Array.isArray(indexes) ? indexes : [indexes])
          .map(index => dataRef.current.rows[index])
          .filter(Boolean)
          .map(row => row.key),
        false
      ),
    selectAll: () =>
      selectRows(
        selectableRows().map(row => row.key),
        true
      ),
    deselectAll: () => clearSelection(),
    clearSelection,
    isRowSelected: key => selectedKeysRef.current.some(item => treeKey(item) === treeKey(key)),
    on: (event, handler) => emitterRef.current.on(event, handler),
    off: (event, handler) => emitterRef.current.off(event, handler),
    getDataSource: () => ({
      items: () => dataRef.current.getTree().nodes,
      get _items() {
        return dataRef.current.getTree().nodes;
      },
      filter: () => combinedFilter(),
      sort: () => sort,
      store: () => storeFacade,
      reload: () => refresh(),
      load: () => refresh(),
      totalCount: () => dataRef.current.getTree().nodes.length,
      isLoading: () => dataRef.current.phase !== "idle",
      pageIndex: () => stateRef.current.pageIndex,
      pageSize: () => stateRef.current.pageSize,
      key: () => sourceRef.current.key,
    }),
    getCombinedFilter: () => combinedFilter(),
    clearFilter: () => setFilterValue(null),
    filter: filterValue => {
      if (filterValue === undefined) return stateRef.current.filterValue;

      setFilterValue(filterValue);

      return undefined;
    },
    clearSorting: () => dispatch({ type: "clearSorting" }),
    state: savedState => {
      if (savedState === undefined) return buildSavedState();

      const initial = createInitialStateNow();

      dispatch({
        type: "reset",
        state: savedState
          ? applySavedState(initial, savedState, columnsRef.current, allowedPageSizesRef.current)
          : initial,
      });

      if (savedState && !propsRef.current.autoExpandAll)
        dataRef.current.setExpandedKeys(savedState.expandedRowKeys || []);

      return undefined;
    },
    option: (name, value) => {
      const { current } = stateRef;

      if (value === undefined) {
        switch (name) {
          case "filterValue":
            return current.filterValue;
          case "filterPanel.filterEnabled":
            return current.filterEnabled;
          case "paging.pageSize":
            return current.pageSize;
          case "paging.pageIndex":
            return current.pageIndex;
          case "selectedRowKeys":
            return selectedKeysRef.current.slice();
          case "expandedRowKeys":
            return dataRef.current.getExpandedKeys();
          case "columns":
            return columnsRef.current;
          case "stateStoring.storageKey":
            return storageKeyRef.current;
          default:
            return propsRef.current[name];
        }
      }

      switch (name) {
        case "filterValue":
          setFilterValue(value);
          break;
        case "filterPanel.filterEnabled":
          dispatch({ type: "filterEnabled", enabled: !!value });
          break;
        case "paging.pageSize":
          dispatch({ type: "pageSize", pageSize: value });
          break;
        case "paging.pageIndex":
          dispatch({ type: "pageIndex", pageIndex: value });
          break;
        case "selectedRowKeys":
          selectRows(value || [], false);
          break;
        case "expandedRowKeys":
          dataRef.current.setExpandedKeys(value || []);
          break;
        default:
          break;
      }

      return undefined;
    },
    columnOption: (identifier, option, value) => {
      const column = findColumn(identifier);

      if (!column) return undefined;

      if (option && typeof option === "object") {
        setColumnOption(column, option);
        return undefined;
      }

      const columnState = getColumnState(column);

      if (value === undefined)
        return option ? columnState[option] ?? column[option] : { ...column, ...columnState };

      setColumnOption(column, { [option]: value });

      return undefined;
    },
    expandAll: () => dataRef.current.setAllExpanded(true),
    collapseAll: () => dataRef.current.setAllExpanded(false),
    expandRow: key => setRowsExpanded(key, true),
    collapseRow: key => setRowsExpanded(key, false),
    isRowExpanded: key => dataRef.current.isExpanded(key),
    pageIndex: index => {
      if (index === undefined) return dataRef.current.pageIndex;

      dispatch({ type: "pageIndex", pageIndex: index });

      return Promise.resolve();
    },
    pageSize: size => {
      if (size === undefined) return stateRef.current.pageSize;

      dispatch({ type: "pageSize", pageSize: size });

      return undefined;
    },
    pageCount: () => dataRef.current.pageCount,
    totalCount: () => dataRef.current.totalCount,
    getRowIndexByKey: key =>
      dataRef.current.rows.findIndex(row => treeKey(row.key) === treeKey(key)),
    getKeyByRowIndex: index => dataRef.current.rows[index]?.key,
    getRowElement: index =>
      tableRef.current?.querySelectorAll(`tbody tr[data-row-index="${index}"]`) || [],
    getCellElement: (rowIndex, identifier) => {
      const column = findColumn(identifier);
      const viewColumn = viewColumnsRef.current.find(item => item.column === column);

      if (!viewColumn) return undefined;

      return tableRef.current?.querySelector(
        `tbody tr[data-row-index="${rowIndex}"] > td[data-col-index="${viewColumn.index}"]`
      );
    },
    updateDimensions: () => setNatural(current => ({ ...current, key: null })),
    beginUpdate: () => undefined,
    endUpdate: () => undefined,
    focus: () => scrollRef.current?.focus(),
    element: () => rootRef.current,
    getScrollable: () => ({
      scrollTo: position =>
        scrollRef.current?.scrollTo(typeof position === "number" ? { top: position } : position),
      scrollTop: () => scrollRef.current?.scrollTop ?? 0,
    }),
  });

  useImperativeHandle(ref, () => ({ instance: instanceRef.current }), []);

  const initializedRef = useRef(false);

  if (!initializedRef.current) {
    initializedRef.current = true;
    props.onInitialized?.({ component: instanceRef.current, element: null });
  }

  useEffect(() => {
    if (data.phase !== "idle" || data.error) return;

    const event = { component: instanceRef.current, element: rootRef.current };

    emitterRef.current.emit("contentReady", event);
    propsRef.current.onContentReady?.(event);
  }, [data.error, data.phase, data.version]);

  const handleBodyClick = useCallback(
    event => {
      if (suppressClickRef.current) {
        suppressClickRef.current = false;
        return;
      }

      const rowElement = event.target.closest("tr[data-row-index]");

      if (!rowElement || !tableRef.current?.contains(rowElement)) return;

      const rowIndex = Number(rowElement.dataset.rowIndex);
      const row = dataRef.current.rows[rowIndex];

      if (!row) return;

      if (event.target.closest(".cg-tree-toggle-active")) {
        setRowsExpanded(row.key, !dataRef.current.isExpanded(row.key));
        return;
      }

      if (event.target.closest(".cg-tree-toggle")) return;

      const component = instanceRef.current;
      const cellElement = event.target.closest("td[data-col-index]");
      const viewColumn = cellElement
        ? viewColumnsRef.current[Number(cellElement.dataset.colIndex)]
        : null;
      const currentMode = selectionModeRef.current;
      const control = event.target.closest(ROW_CONTROL_SELECTOR);
      const fromControl = !!control && rowElement.contains(control);

      if (currentMode !== "none" && !fromControl && isRowSelectable(row)) {
        const selected = selectedKeysRef.current.some(key => treeKey(key) === treeKey(row.key));

        if (currentMode === "single") {
          if ((event.ctrlKey || event.metaKey) && selected) deselectRows([row.key]);
          else selectRows([row.key], false);
        } else if (event.shiftKey && lastClickedIndexRef.current != null) {
          const [from, to] = [lastClickedIndexRef.current, rowIndex].sort((a, b) => a - b);

          selectRows(
            dataRef.current.rows
              .slice(from, to + 1)
              .filter(item => isRowSelectable(item))
              .map(item => item.key),
            true
          );
        } else if (selected) deselectRows([row.key]);
        else selectRows([row.key], true);

        lastClickedIndexRef.current = rowIndex;
      }

      const isSelected = selectedKeysRef.current.some(key => treeKey(key) === treeKey(row.key));
      const isExpanded = dataRef.current.isExpanded(row.key);
      const rowEvent = {
        component,
        element: rootRef.current,
        rowElement,
        rowIndex,
        rowType: "data",
        data: row.data,
        key: row.key,
        node: row,
        level: row.level,
        event,
        isSelected,
        isExpanded,
        values: [],
      };

      if (viewColumn && propsRef.current.onCellClick) {
        const value =
          viewColumn.kind === "data" ? calculateCellValue(viewColumn.column, row.data) : undefined;

        propsRef.current.onCellClick({
          ...rowEvent,
          cellElement,
          column: viewColumn.column,
          columnIndex: viewColumn.index,
          value,
          displayValue: value,
          text:
            viewColumn.kind === "data" && value != null
              ? getValueText(viewColumn.column, value, localeRef.current)
              : "",
          row: {
            data: row.data,
            key: row.key,
            node: row,
            rowIndex,
            rowType: "data",
            isExpanded,
          },
        });
      }

      propsRef.current.onRowClick?.(rowEvent);
    },
    [deselectRows, isRowSelectable, selectRows, setRowsExpanded]
  );

  const handleBodyDoubleClick = useCallback(event => {
    const rowElement = event.target.closest("tr[data-row-index]");

    if (!rowElement || !propsRef.current.onRowDblClick) return;
    if (event.target.closest(".cg-tree-toggle")) return;

    const rowIndex = Number(rowElement.dataset.rowIndex);
    const row = dataRef.current.rows[rowIndex];

    if (!row) return;

    propsRef.current.onRowDblClick({
      component: instanceRef.current,
      element: rootRef.current,
      rowElement,
      rowIndex,
      rowType: "data",
      data: row.data,
      key: row.key,
      node: row,
      event,
    });
  }, []);

  const handleBodyMouseOver = useCallback(event => {
    const text = event.target.closest(".cg-tree-text");
    const cell = text || event.target.closest("td");

    if (!cell || (!text && cell.childElementCount)) return;

    if (cell.scrollWidth > cell.clientWidth) cell.title = cell.textContent;
    else cell.removeAttribute("title");
  }, []);

  useEffect(() => {
    const body = bodyRef.current;

    if (!body) return undefined;

    body.addEventListener("click", handleBodyClick);
    body.addEventListener("dblclick", handleBodyDoubleClick);
    body.addEventListener("mouseover", handleBodyMouseOver);

    return () => {
      body.removeEventListener("click", handleBodyClick);
      body.removeEventListener("dblclick", handleBodyDoubleClick);
      body.removeEventListener("mouseover", handleBodyMouseOver);
    };
  }, [handleBodyClick, handleBodyDoubleClick, handleBodyMouseOver]);

  const resolveDropTarget = useCallback((clientX, clientY, fromIndex, item, itemElement) => {
    const element = document.elementFromPoint(clientX, clientY);
    const rowElement = element?.closest?.("tbody tr[data-row-index]");

    if (!rowElement || !bodyRef.current?.contains(rowElement)) return null;

    const index = Number(rowElement.dataset.rowIndex);
    const rect = rowElement.getBoundingClientRect();
    const offset = clientY - rect.top;
    const allowInside = !!rowDraggingRef.current.allowDropInsideItem;
    const inside = allowInside && offset > rect.height / 3 && offset < (rect.height * 2) / 3;
    const slot = offset < rect.height / 2 ? index : index + 1;

    if (inside && index === fromIndex) return null;

    const toIndex = inside ? index : slot > fromIndex ? slot - 1 : slot;

    if (!inside && toIndex === fromIndex) return null;

    const event = {
      component: instanceRef.current,
      fromIndex,
      toIndex,
      dropInsideItem: inside,
      itemData: item.data,
      itemElement,
      cancel: false,
    };

    rowDraggingRef.current.onDragChange?.(event);

    return { index, slot, inside, toIndex, valid: !event.cancel };
  }, []);

  const startRowDrag = useCallback(
    (downEvent, rowElement) => {
      const fromIndex = Number(rowElement.dataset.rowIndex);
      const item = dataRef.current.rows[fromIndex];

      if (!item) return;

      const touch = downEvent.pointerType === "touch";
      const { pointerId } = downEvent;
      const startX = downEvent.clientX;
      const startY = downEvent.clientY;
      let active = false;
      let ghost = null;
      let target = null;
      let targetKey = null;
      let timer = null;
      let lastX = startX;
      let lastY = startY;

      const moveGhost = () => {
        if (ghost) ghost.style.transform = `translate(${lastX + 14}px, ${lastY - 16}px)`;
      };

      const begin = () => {
        active = true;
        window.getSelection?.()?.removeAllRanges();
        ghost = createDragGhost(rowElement, rootRef.current);
        moveGhost();
        rootRef.current?.classList.add("cg-dragging-active");
        setDragState({ from: fromIndex, target: null });
      };

      const autoScroll = clientY => {
        const scrollElement = scrollRef.current;

        if (scrollElement && scrollElement.scrollHeight > scrollElement.clientHeight) {
          const rect = scrollElement.getBoundingClientRect();

          if (clientY < rect.top + ROW_HEIGHT * 2) scrollElement.scrollTop -= 12;
          else if (clientY > rect.bottom - ROW_HEIGHT) scrollElement.scrollTop += 12;
        }

        if (clientY < ROW_HEIGHT) window.scrollBy(0, -12);
        else if (clientY > window.innerHeight - ROW_HEIGHT) window.scrollBy(0, 12);
      };

      const update = () => {
        autoScroll(lastY);
        moveGhost();
        target = resolveDropTarget(lastX, lastY, fromIndex, item, rowElement);
        rootRef.current?.classList.toggle("cg-drop-invalid", !!target && !target.valid);

        const nextKey = target
          ? `${target.index}|${target.slot}|${target.inside}|${target.valid}`
          : null;

        if (nextKey === targetKey) return;

        targetKey = nextKey;
        setDragState({ from: fromIndex, target });
      };

      const preventTouchScroll = event => {
        if (active) event.preventDefault();
      };

      const cleanup = () => {
        clearTimeout(timer);
        window.removeEventListener("pointermove", handleMove);
        window.removeEventListener("pointerup", handleUp);
        window.removeEventListener("pointercancel", handleCancel);
        window.removeEventListener("keydown", handleKeyDown);
        window.removeEventListener("touchmove", preventTouchScroll);
        ghost?.remove();
        rootRef.current?.classList.remove("cg-dragging-active", "cg-drop-invalid");
      };

      function handleMove(event) {
        if (event.pointerId !== pointerId) return;

        lastX = event.clientX;
        lastY = event.clientY;

        if (!active) {
          const distance = Math.hypot(lastX - startX, lastY - startY);

          if (touch) {
            if (distance > TREE_DRAG_THRESHOLD) cleanup();
            return;
          }

          if (distance < TREE_DRAG_THRESHOLD) return;

          begin();
        }

        update();
      }

      function handleUp(event) {
        if (event.pointerId !== pointerId) return;

        cleanup();

        if (!active) return;

        suppressClickRef.current = true;
        setTimeout(() => {
          suppressClickRef.current = false;
        }, 0);
        setDragState(null);

        if (!target || !target.valid) return;

        const dropEvent = {
          component: instanceRef.current,
          fromIndex,
          toIndex: target.toIndex,
          dropInsideItem: target.inside,
          itemData: item.data,
          itemElement: rowElement,
          cancel: false,
        };

        rowDraggingRef.current.onDragEnd?.(dropEvent);

        if (dropEvent.cancel) return;

        rowDraggingRef.current.onReorder?.({ ...dropEvent, cancel: false });
      }

      function handleCancel(event) {
        if (event.pointerId !== pointerId) return;

        cleanup();
        setDragState(null);
      }

      function handleKeyDown(event) {
        if (event.key !== "Escape") return;

        cleanup();
        setDragState(null);
      }

      window.addEventListener("pointermove", handleMove);
      window.addEventListener("pointerup", handleUp);
      window.addEventListener("pointercancel", handleCancel);
      window.addEventListener("keydown", handleKeyDown);

      if (touch) {
        window.addEventListener("touchmove", preventTouchScroll, { passive: false });
        timer = setTimeout(() => {
          begin();
          update();
        }, TREE_TOUCH_DRAG_DELAY);
      }
    },
    [resolveDropTarget]
  );

  useEffect(() => {
    const body = bodyRef.current;

    if (!body || !rowDraggingEnabled) return undefined;

    const handlePointerDown = event => {
      if (event.button !== 0 || event.isPrimary === false) return;

      const rowElement = event.target.closest("tr[data-row-index]");

      if (!rowElement || !body.contains(rowElement)) return;
      if (event.target.closest(NO_DRAG_SELECTOR)) return;

      if (event.pointerType !== "touch") event.preventDefault();

      startRowDrag(event, rowElement);
    };

    const handleNativeDrag = event => event.preventDefault();

    body.addEventListener("pointerdown", handlePointerDown);
    body.addEventListener("dragstart", handleNativeDrag);

    return () => {
      body.removeEventListener("pointerdown", handlePointerDown);
      body.removeEventListener("dragstart", handleNativeDrag);
    };
  }, [rowDraggingEnabled, startRowDrag]);

  const clickable = !!onRowClick || selectionMode !== "none";
  const rowCtx = useMemo(
    () => ({
      instance: instanceRef.current,
      locale,
      t,
      handlers: handlersRef,
      clickable,
      selectionMode,
      toggleRowSelection,
    }),
    [clickable, locale, selectionMode, t, toggleRowSelection]
  );

  const applyFilterRow = useCallback(
    (column, operation, value) => {
      const nextOperation = operation || DEFAULT_OPERATION[column.dataType] || "=";

      setFilterValue(
        setFilterRowCondition(stateRef.current.filterValue, column, nextOperation, value)
      );
    },
    [setFilterValue]
  );

  const applyHeaderFilter = useCallback(
    (column, type, values) =>
      setFilterValue(setHeaderFilterCondition(stateRef.current.filterValue, column, type, values)),
    [setFilterValue]
  );

  const loadHeaderFilterValues = useCallback(
    async column => {
      const translate = tRef.current;

      if (column.headerFilterDataSource) {
        const list =
          typeof column.headerFilterDataSource === "function"
            ? await column.headerFilterDataSource()
            : column.headerFilterDataSource;

        return (list || []).map(item => ({
          value: item.value,
          text: item.text ?? String(item.value),
        }));
      }

      if (column.dataType === "boolean")
        return [true, false].map(value => ({
          value,
          text: value ? column.trueText ?? translate("yes") : column.falseText ?? translate("no"),
        }));

      const otherFilter = stateRef.current.filterEnabled
        ? buildDataSourceFilter(
            setHeaderFilterCondition(stateRef.current.filterValue, column, "anyof", []),
            columnsMap(columnsRef.current),
            "headerFilter"
          )
        : null;
      const selector = getColumnSelector(column);
      const date = isDateType(column.dataType);
      const group = date
        ? [
            { selector, groupInterval: "year", isExpanded: true },
            { selector, groupInterval: "month", isExpanded: true },
            { selector, groupInterval: "day", isExpanded: false },
          ]
        : [{ selector, desc: false, isExpanded: false }];
      const groupsResult = queryLocal(getItems(), { filter: otherFilter, group }, getGetter).data;

      if (date) {
        const nodes = [];

        (groupsResult || []).forEach(year => {
          if (year.key == null) {
            nodes.push({ value: null, text: translate("gridBlank") });
            return;
          }

          nodes.push({
            value: String(year.key),
            text: String(year.key),
            children: (year.items || []).map(month => ({
              value: `${year.key}/${month.key}`,
              month: month.key,
              text: String(month.key),
              children: (month.items || []).map(day => ({
                value: `${year.key}/${month.key}/${day.key}`,
                text: String(day.key),
              })),
            })),
          });
        });

        return nodes;
      }

      return (groupsResult || []).map(item => ({
        value: item.key,
        text:
          item.key == null || item.key === ""
            ? translate("gridBlank")
            : getValueText(column, item.key, localeRef.current, "headerFilter"),
      }));
    },
    [getGetter, getItems]
  );

  const dropColumn = useCallback((draggedId, targetId, before) => {
    const { current } = stateRef;

    if (!current.columns[targetId]) return;

    const ordered = columnsRef.current
      .slice()
      .sort((a, b) => current.columns[a.id].order - current.columns[b.id].order)
      .filter(column => column.id !== draggedId);
    const targetPosition = ordered.findIndex(column => column.id === targetId);

    dispatch({
      type: "move",
      id: draggedId,
      targetOrder: before ? targetPosition : targetPosition + 1,
      columns: columnsRef.current,
    });
  }, []);

  const startResize = useCallback((event, column, headerCell) => {
    if (event.button !== 0 || !headerCell) return;

    event.preventDefault();
    event.stopPropagation();

    const table = tableRef.current;
    const viewColumn = viewColumnsRef.current.find(item => item.key === column.id);

    if (!table || !viewColumn) return;

    const col = table.querySelector(`colgroup > col[data-col-index="${viewColumn.index}"]`);
    const handle = event.currentTarget;
    const startX = event.clientX;
    const startWidth = headerCell.getBoundingClientRect().width;
    const startTableWidth = table.getBoundingClientRect().width;
    const minWidth = Math.max(column.minWidth ?? 0, 40);
    let widthNow = startWidth;

    handle.classList.add("cg-resizing");

    const handleMove = moveEvent => {
      widthNow = Math.max(startWidth + moveEvent.clientX - startX, minWidth);

      if (col) col.style.width = `${widthNow}px`;
      table.style.width = `${startTableWidth + widthNow - startWidth}px`;
    };

    const handleUp = () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      handle.classList.remove("cg-resizing");

      const { widths } = layoutRef.current;

      visibleColumnsRef.current.forEach(item => {
        if (
          item.id !== column.id &&
          !stateRef.current.columns[item.id]?.width &&
          !parseWidth(item.width)
        )
          dispatch({ type: "columnOption", id: item.id, patch: { width: widths[item.id] } });
      });

      dispatch({ type: "columnOption", id: column.id, patch: { width: Math.round(widthNow) } });
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
  }, []);

  const autoFitColumn = useCallback(column => {
    dispatch({ type: "columnOption", id: column.id, patch: { width: undefined } });
    setNatural(current => ({ ...current, key: null }));
  }, []);

  const toggleSort = useCallback(
    (column, event) => {
      const columnState = stateRef.current.columns[column.id];
      const multiple = sortingMode === "multiple" && (event.shiftKey || event.ctrlKey);

      if ((event.ctrlKey || event.metaKey) && !multiple) {
        dispatch({ type: "sort", id: column.id, order: null, multiple: false });
        return;
      }

      dispatch({
        type: "sort",
        id: column.id,
        order: columnState?.sortOrder === "asc" ? "desc" : "asc",
        multiple,
      });
    },
    [sortingMode]
  );

  const sortCount = Object.values(state.columns).filter(
    columnState => columnState.sortOrder
  ).length;

  const hasColumnMenu = column =>
    column.hasData &&
    ((sortingMode !== "none" && column.allowSorting) ||
      columnFixingEnabled ||
      columnChooserEnabled);

  const allowSelectAll = selectionMode === "multiple" && selectionOptions.allowSelectAll !== false;

  const headerPrefix = viewColumn => {
    if (!allowSelectAll || !viewColumn.treeColumn) return null;

    const keys = selectableRows().map(row => treeKey(row.key));
    const count = keys.filter(key => selectedSet.has(key)).length;
    const all = keys.length > 0 && count === keys.length;

    return (
      <Checkbox
        size="small"
        checked={all}
        indeterminate={!all && count > 0}
        disabled={!keys.length}
        onChange={event => {
          if (event.target.checked) instanceRef.current.selectAll();
          else clearSelection();
        }}
        inputProps={{ "aria-label": t("gridSelectAll") }}
        sx={{ p: 0.5, mr: 0.5 }}
      />
    );
  };

  const headerCtx = {
    instance: instanceRef.current,
    t,
    sortingMode,
    multipleSort: sortingMode === "multiple",
    sortCount,
    headerFilterVisible,
    hasColumnMenu,
    allowColumnResizing,
    allowColumnReordering,
    allowSelectAll: false,
    headerPrefix,
    isColumnFiltered: column => !!getHeaderFilterCondition(state.filterValue, column),
    canDragColumn: column => allowColumnReordering && column.allowReordering,
    setDraggedColumn: columnId => {
      draggedColumnRef.current = columnId;
    },
    getDraggedColumn: () => draggedColumnRef.current,
    dropColumn,
    toggleSort,
    openColumnMenu: (column, anchorEl, position) => setMenuTarget({ column, anchorEl, position }),
    openHeaderFilter: (column, anchorEl) => setHeaderFilterTarget({ column, anchorEl }),
    startResize,
    autoFitColumn,
  };

  const filterCtx = {
    t,
    locale,
    getFilterRowCondition: column => getFilterRowCondition(state.filterValue, column),
    getSelectedOperation: column =>
      selectedOperationsRef.current.get(column.id) ||
      column.selectedFilterOperation ||
      DEFAULT_OPERATION[column.dataType] ||
      "=",
    setSelectedOperation: (column, operation) => {
      if (operation) selectedOperationsRef.current.set(column.id, operation);
      else selectedOperationsRef.current.delete(column.id);
    },
    applyFilterRow,
  };

  const openColumnChooser = anchor =>
    setChooserAnchor(
      anchor ||
        rootRef.current?.querySelector(".cg-toolbar [data-grid-chooser]") ||
        rootRef.current?.querySelector(".cg-header-row") ||
        rootRef.current
    );

  const menuCtx = {
    t,
    sortingMode,
    groupingEnabled: false,
    groupCount: 0,
    columnFixingEnabled,
    columnChooserEnabled,
    allowColumnResizing,
    getColumnState,
    setSort: (column, order) => dispatch({ type: "sort", id: column.id, order, multiple: false }),
    group: () => undefined,
    ungroup: () => undefined,
    ungroupAll: () => undefined,
    fixColumn: (column, position) =>
      setColumnOption(
        column,
        position ? { fixed: true, fixedPosition: position } : { fixed: false }
      ),
    hideColumn: column => setColumnOption(column, { visible: false }),
    openColumnChooser: () => openColumnChooser(),
    autoFitColumn,
  };

  const headerFilterCtx = useMemo(
    () => ({
      t,
      language: i18n.language,
      headerFilterAllowSearch: childOptions.headerFilter?.allowSearch,
      loadHeaderFilterValues,
      getHeaderFilterCondition: column =>
        getHeaderFilterCondition(stateRef.current.filterValue, column),
      applyHeaderFilter,
    }),
    [
      applyHeaderFilter,
      childOptions.headerFilter?.allowSearch,
      i18n.language,
      loadHeaderFilterValues,
      t,
    ]
  );

  const filterableColumns = useMemo(
    () =>
      columns.filter(
        column =>
          column.hasData &&
          (column.allowFiltering || column.allowHeaderFiltering) &&
          getColumnSelector(column)
      ),
    [columns]
  );

  const panelCtx = {
    t,
    locale,
    filterValue: state.filterValue,
    filterEnabled: state.filterEnabled,
    columnsBySelector,
    filterableColumns,
    headerFilter: headerFilterCtx,
    setFilterValue,
    setFilterEnabled: enabled => dispatch({ type: "filterEnabled", enabled }),
    clearFilter,
    openFilterBuilder: () => setBuilderOpen(true),
  };

  const toolbarCtx = {
    t,
    groupPanelVisible: false,
    groupedColumns: [],
    groupToggleVisible: false,
    exportEnabled: false,
    exportFormats: [],
    columnChooserEnabled,
    openColumnChooser,
  };

  const items = [
    ...(toolbarItems || []),
    ...(childOptions.toolbar?.items || DEFAULT_TOOLBAR_ITEMS),
  ];

  const fixedHeight = height != null && height !== "inherit";

  const rootClasses = ["consuman-grid", "consuman-tree-list"];

  if (showBorders) rootClasses.push("cg-bordered");
  if (showColumnLines) rootClasses.push("cg-column-lines");
  if (showRowLines) rootClasses.push("cg-row-lines");
  if (rowAlternationEnabled) rootClasses.push("cg-row-alternation");
  if (hoverStateEnabled) rootClasses.push("cg-hover");
  if (className) rootClasses.push(className);

  const showSkeleton = data.phase === "initial" && data.rows.length === 0 && !data.error;
  const showEmpty =
    !showSkeleton && data.phase !== "initial" && data.rows.length === 0 && !data.error;
  const reloading =
    loadPanelEnabled &&
    (data.phase === "reloading" || (data.phase === "initial" && data.rows.length > 0));
  const showPager =
    paged &&
    (pagerOptions.visible === true || (pagerOptions.visible !== false && data.pageCount > 1));

  const dropTarget = dragState?.target?.valid ? dragState.target : null;
  let dropIndicatorTop = null;

  if (dropTarget && !dropTarget.inside && tableRef.current) {
    const lastIndex = Math.min(dropTarget.slot, data.rows.length - 1);
    const rowElement = tableRef.current.querySelector(`tbody tr[data-row-index="${lastIndex}"]`);

    if (rowElement)
      dropIndicatorTop =
        dropTarget.slot >= data.rows.length
          ? rowElement.offsetTop + rowElement.offsetHeight
          : rowElement.offsetTop;
  }

  return (
    <GridRoot
      ref={rootRef}
      id={id}
      className={rootClasses.join(" ")}
      style={{ height: fixedHeight ? height : undefined, width, ...style }}
    >
      <GridToolbar
        ctx={toolbarCtx}
        items={items}
      />
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          flex: "1 1 auto",
          minHeight: 0,
        }}
      >
        {reloading ? (
          <Box
            className="cg-loading"
            sx={{
              top: filterRowVisible
                ? `calc(var(--cg-header-height, ${ROW_HEIGHT}px) + ${ROW_HEIGHT}px)`
                : `var(--cg-header-height, ${ROW_HEIGHT}px)`,
            }}
          >
            <Box className="cg-loading-panel">
              <CircularProgress size={32} />
            </Box>
          </Box>
        ) : null}
        <Box
          ref={scrollRef}
          className="cg-scroll"
          tabIndex={-1}
        >
          <table
            ref={tableRef}
            className={`cg-table${measuring ? " cg-measuring" : ""}`}
            style={measuring ? undefined : { width: layout.total }}
            role="grid"
            aria-busy={data.phase !== "idle"}
            aria-multiselectable={selectionMode === "multiple" || undefined}
          >
            <colgroup>
              {viewColumns.map(viewColumn => {
                const autoWidth =
                  measuring &&
                  viewColumn.kind === "data" &&
                  !parseWidth(viewColumn.column.width) &&
                  !state.columns[viewColumn.key]?.width;

                return (
                  <col
                    key={viewColumn.key}
                    data-col-index={viewColumn.index}
                    style={autoWidth ? undefined : { width: viewColumn.width }}
                  />
                );
              })}
            </colgroup>
            <thead>
              <HeaderRow
                viewColumns={viewColumns}
                ctx={headerCtx}
              />
              {filterRowVisible ? (
                <FilterRow
                  viewColumns={viewColumns}
                  ctx={filterCtx}
                />
              ) : null}
            </thead>
            <tbody ref={bodyRef}>
              {data.rows.map((row, rowIndex) => {
                const rowKey = treeKey(row.key);

                return (
                  <TreeDataRow
                    key={`n:${rowKey}`}
                    row={row}
                    rowIndex={rowIndex}
                    viewColumns={viewColumns}
                    ctx={rowCtx}
                    selected={selectedSet.has(rowKey)}
                    selectable={selectionMode === "multiple" ? isRowSelectable(row) : true}
                    expanded={data.isExpanded(row.key)}
                    alternate={rowIndex % 2 === 1}
                    version={renderVersion * 100000 + (rowVersionsRef.current.get(rowKey) || 0)}
                    dragging={dragState?.from === rowIndex}
                    dropInside={!!dropTarget?.inside && dropTarget.index === rowIndex}
                  />
                );
              })}
              {showSkeleton
                ? Array.from({ length: 5 }, (_, index) => (
                    <tr key={`skeleton${index}`}>
                      {viewColumns.map(viewColumn => (
                        <td key={viewColumn.key}>
                          {viewColumn.kind === "data" ? (
                            <Skeleton
                              variant="text"
                              sx={{ width: `${55 + ((index * 17 + viewColumn.index * 13) % 40)}%` }}
                            />
                          ) : null}
                        </td>
                      ))}
                    </tr>
                  ))
                : null}
              {data.error ? (
                <tr>
                  <td colSpan={viewColumns.length}>
                    <Box
                      className="cg-empty"
                      sx={{ width: containerWidth || "100%", color: "error.main" }}
                    >
                      <Iconify
                        icon="eva:alert-triangle-outline"
                        width={20}
                      />
                      <Typography variant="body2">{t("gridLoadError")}</Typography>
                      <Button
                        size="small"
                        variant="soft"
                        color="error"
                        onClick={() => data.retry()}
                      >
                        {t("gridRetry")}
                      </Button>
                    </Box>
                  </td>
                </tr>
              ) : null}
              {showEmpty ? (
                <tr>
                  <td colSpan={viewColumns.length}>
                    <Box
                      className="cg-empty"
                      sx={{ width: containerWidth || "100%" }}
                    >
                      <Iconify
                        icon="eva:inbox-outline"
                        width={22}
                      />
                      <Typography variant="body2">{noDataText || t("gridNoData")}</Typography>
                    </Box>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          {dropIndicatorTop != null ? (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                top: dropIndicatorTop - 1,
                height: 2,
                bgcolor: "primary.main",
                pointerEvents: "none",
                zIndex: 6,
              }}
            />
          ) : null}
        </Box>
      </Box>
      {filterPanelVisible ? (
        <FilterPanel
          ctx={panelCtx}
          summary=""
        />
      ) : null}
      {showPager ? (
        <Pager
          pageIndex={data.pageIndex}
          pageCount={data.pageCount}
          pageSize={state.pageSize}
          totalCount={data.totalCount}
          allowedPageSizes={allowedPageSizes}
          showPageSizeSelector={!!pagerOptions.showPageSizeSelector}
          onPageChange={pageIndex => dispatch({ type: "pageIndex", pageIndex })}
          onPageSizeChange={pageSize => dispatch({ type: "pageSize", pageSize })}
          t={t}
        />
      ) : null}
      <ColumnMenu
        target={menuTarget}
        ctx={menuCtx}
        onClose={() => setMenuTarget(null)}
      />
      <HeaderFilterPopover
        target={headerFilterTarget}
        ctx={headerFilterCtx}
        onClose={() => setHeaderFilterTarget(null)}
      />
      {chooserAnchor ? (
        <ColumnChooser
          anchorEl={chooserAnchor}
          columns={columns}
          ctx={{
            t,
            getColumnState,
            setColumnVisible: (column, visible) => setColumnOption(column, { visible }),
          }}
          onClose={() => setChooserAnchor(null)}
        />
      ) : null}
      <FilterBuilderDialog
        open={builderOpen}
        ctx={panelCtx}
        onClose={() => setBuilderOpen(false)}
      />
    </GridRoot>
  );
});

TreeList.propTypes = {
  children: PropTypes.node,
  dataSource: PropTypes.any,
  keyExpr: PropTypes.any,
  parentIdExpr: PropTypes.string,
  rootValue: PropTypes.any,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  id: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  showBorders: PropTypes.bool,
  showColumnLines: PropTypes.bool,
  showRowLines: PropTypes.bool,
  rowAlternationEnabled: PropTypes.bool,
  hoverStateEnabled: PropTypes.bool,
  allowColumnResizing: PropTypes.bool,
  allowColumnReordering: PropTypes.bool,
  noDataText: PropTypes.string,
  filterValue: PropTypes.any,
  defaultFilterValue: PropTypes.any,
  toolbarItems: PropTypes.array,
  locale: PropTypes.object,
  filterMode: PropTypes.oneOf(["withAncestors", "fullBranch", "matchOnly"]),
  autoExpandAll: PropTypes.bool,
  expandNodesOnFiltering: PropTypes.bool,
  defaultExpandedRowKeys: PropTypes.array,
  defaultSelectedRowKeys: PropTypes.array,
  onRowClick: PropTypes.func,
  onRowPrepared: PropTypes.func,
  onInitialized: PropTypes.func,
};

export default TreeList;
